import { useCallback, useMemo, useRef, useState } from "react";
import type { PendingApproval } from "../../shared/types";
const MAX_FILE_SIZE_BYTES = 30 * 1024 * 1024;
export const MAX_IMAGE_COUNT = 10;
const ALLOWED_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/tiff",
  "image/bmp",
]);
const ALLOWED_EXTENSIONS_LABEL = "JPEG, PNG, GIF, TIFF, BMP";

interface FileEntry {
  id: string;
  file: File;
  status: "uploading" | "uploaded" | "error";
  location?: string;
  error?: string;
}

interface ImageCollectionCardProps {
  pendingApproval: PendingApproval & { error?: string };
  agentId: string;
  conversationId: string;
  baseUrl: string;
  onApprove?: (
    decision: "approved" | "denied",
    clientArtifacts?: Record<string, string>,
    toolResult?: string,
  ) => void;
  isLoading: boolean;
  primaryColor: string;
}

function validateFileTypeAndSize(file: File): string | null {
  if (!ALLOWED_MIME_TYPES.has(file.type)) {
    return `"${file.name}" is not a supported image type. Use ${ALLOWED_EXTENSIONS_LABEL}.`;
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `"${file.name}" exceeds the 30 MB size limit.`;
  }
  return null;
}

async function resolveUploadErrorMessage(response: Response): Promise<string> {
  if (response.status === 403) {
    return "This upload is no longer available. Please refresh and try again.";
  }
  if (response.status === 502) {
    return "We couldn't upload your image right now. Please try again in a few minutes.";
  }
  try {
    const body = (await response.json()) as { detail?: string; title?: string };
    if (body.detail) return body.detail;
    if (body.title) return body.title;
  } catch {
    // ignore parse errors; use fallback message
  }
  return `Upload failed (${response.status})`;
}

function FileStatusBadge({ entry }: { entry: FileEntry }) {
  if (entry.status === "uploading") {
    return (
      <span className="text-[#5A5A5A]" aria-live="polite">
        Uploading…
      </span>
    );
  }

  if (entry.status === "uploaded") {
    return <span className="text-green-600">Uploaded</span>;
  }

  if (entry.status === "error") {
    return (
      <span className="text-red-600" data-testid="upload-error" role="alert">
        {entry.error ?? "Upload failed"}
      </span>
    );
  }

  return null;
}

export function ImageCollectionCard({
  pendingApproval,
  agentId,
  conversationId,
  baseUrl,
  onApprove,
  isLoading,
  primaryColor,
}: ImageCollectionCardProps) {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const fileCountRef = useRef(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isUploading = files.some((f) => f.status === "uploading");
  const hasUploadErrors = files.some((f) => f.status === "error");
  const uploadedLocations = useMemo(
    () =>
      files
        .filter((f) => f.status === "uploaded")
        .map((f) => f.location)
        .filter((loc): loc is string => Boolean(loc)),
    [files],
  );

  const uploadFile = useCallback(
    async (entry: FileEntry) => {
      const formData = new FormData();
      formData.append("file", entry.file);

      try {
        const response = await fetch(
          `${baseUrl}/api/v1/public/agents/${agentId}/conversations/${conversationId}/scf-image-upload`,
          {
            method: "POST",
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error(await resolveUploadErrorMessage(response));
        }

        const data = (await response.json()) as { location?: string | null };
        if (
          typeof data.location !== "string" ||
          data.location.trim().length === 0
        ) {
          throw new Error("Upload failed: server returned no location.");
        }
        setFiles((prev) => {
          if (!prev.some((f) => f.id === entry.id)) return prev;
          return prev.map((f) =>
            f.id === entry.id
              ? { ...f, status: "uploaded", location: data.location as string }
              : f,
          );
        });
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Upload failed. Please try again.";
        setFiles((prev) => {
          if (!prev.some((f) => f.id === entry.id)) return prev;
          return prev.map((f) =>
            f.id === entry.id ? { ...f, status: "error", error: message } : f,
          );
        });
      }
    },
    [agentId, baseUrl, conversationId],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValidationError(null);
      const selected = Array.from(e.target.files ?? []);
      if (selected.length === 0) return;

      const candidates: FileEntry[] = [];
      const errors: string[] = [];

      for (const file of selected) {
        const error = validateFileTypeAndSize(file);
        if (error) {
          errors.push(error);
        } else {
          candidates.push({
            id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
            file,
            status: "uploading",
          });
        }
      }

      e.target.value = "";

      if (errors.length > 0) {
        setValidationError(errors[0]);
      }

      if (candidates.length === 0) return;

      const currentCount = fileCountRef.current;
      const remaining = MAX_IMAGE_COUNT - currentCount;

      if (remaining <= 0) {
        setValidationError(
          `You can upload a maximum of ${MAX_IMAGE_COUNT} images.`,
        );
        return;
      }

      const accepted = candidates.slice(0, remaining);
      if (accepted.length < candidates.length) {
        setValidationError(
          `You can upload a maximum of ${MAX_IMAGE_COUNT} images.`,
        );
      }

      setFiles((prev) => {
        const next = [...prev, ...accepted];
        fileCountRef.current = next.length;
        return next;
      });

      for (const entry of accepted) {
        void uploadFile(entry);
      }
    },
    [uploadFile],
  );

  const handleRemove = useCallback((id: string) => {
    setFiles((prev) => {
      const next = prev.filter((f) => f.id !== id);
      fileCountRef.current = next.length;
      return next;
    });
  }, []);

  const handleSubmit = useCallback(() => {
    onApprove?.("approved", undefined, JSON.stringify(uploadedLocations));
  }, [onApprove, uploadedLocations]);

  const handleSkip = useCallback(() => {
    onApprove?.("approved", undefined, "[]");
  }, [onApprove]);

  const canSubmit =
    !isLoading &&
    !isUploading &&
    !hasUploadErrors &&
    uploadedLocations.length > 0;

  const atMaxCount = files.length >= MAX_IMAGE_COUNT;

  return (
    <div
      className="rounded-xl border border-[#E0E0E0] bg-white p-4 shadow-sm"
      role="region"
      aria-label="Collect issue images"
      data-testid="image-collection-card"
    >
      <p className="mb-3 text-sm font-semibold text-[#232323]">
        Add photos of the issue
      </p>
      <p className="mb-4 text-sm text-[#5A5A5A]">
        Upload one or more photos to help describe the issue. Accepted formats:{" "}
        {ALLOWED_EXTENSIONS_LABEL}. Max 30 MB per file. Up to {MAX_IMAGE_COUNT}{" "}
        images.
      </p>

      <div className="mb-3">
        <label
          className={`relative inline-block rounded border border-[#E0E0E0] bg-white px-4 py-2 text-sm font-medium text-[#232323] transition-colors focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 ${atMaxCount ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-[#F5F5F5]"}`}
        >
          Choose files
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/tiff,image/bmp"
            multiple
            disabled={atMaxCount}
            className="absolute inset-0 h-full w-full opacity-0"
            onChange={handleFileChange}
            data-testid="file-input"
          />
        </label>
      </div>

      {atMaxCount && (
        <p
          className="mb-3 text-xs text-[#5A5A5A]"
          data-testid="max-count-message"
        >
          Maximum of {MAX_IMAGE_COUNT} images reached.
        </p>
      )}

      {validationError && (
        <p
          className="mb-3 text-xs text-red-600"
          data-testid="validation-error"
          role="alert"
        >
          {validationError}
        </p>
      )}

      {files.length > 0 && (
        <ul className="mb-3 space-y-2" aria-label="Selected files">
          {files.map((entry) => (
            <li
              key={entry.id}
              className="flex items-center justify-between gap-2 rounded border border-[#E0E0E0] px-3 py-2 text-sm"
              data-testid={`file-entry-${entry.status}`}
            >
              <span className="min-w-0 flex-1 truncate text-[#232323]">
                {entry.file.name}
              </span>
              <span className="shrink-0 text-xs">
                <FileStatusBadge entry={entry} />
              </span>
              {entry.status !== "uploading" && (
                <button
                  type="button"
                  onClick={() => handleRemove(entry.id)}
                  className="shrink-0 text-xs text-[#5A5A5A] hover:text-[#232323]"
                  aria-label={`Remove ${entry.file.name}`}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {pendingApproval.error && (
        <p
          className="mb-3 text-xs text-red-600"
          data-testid="approval-error"
          role="alert"
        >
          {pendingApproval.error}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{ backgroundColor: primaryColor }}
          className="rounded px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Submit photos"
          data-testid="submit-button"
        >
          Submit
        </button>
        <button
          type="button"
          onClick={handleSkip}
          disabled={isLoading || isUploading}
          className="rounded border border-[#E0E0E0] bg-white px-4 py-2 text-sm font-medium text-[#232323] transition-colors hover:bg-[#F5F5F5] disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Skip photo upload"
          data-testid="skip-button"
        >
          Skip
        </button>
      </div>
    </div>
  );
}
